import React, { useState, useEffect } from 'react';
import AiAgentService from '../../api/AiAgentService';
import './style.css';
import { MdClose } from "react-icons/md";

// Список доступних моделей (можна розширити)
const availableModels = [
    { id: 'gpt-4', name: 'GPT-4' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
    { id: 'claude-3', name: 'Claude 3' }
];

const NoteCreationModal = ({ 
    isOpen, 
    onClose, 
    userMessage, 
    aiMessage, 
    messageId 
}) => {
    const [authors, setAuthors] = useState([]);
    const [selectedAuthorId, setSelectedAuthorId] = useState(() => {
        // Завантажуємо збереженого автора з localStorage
        const savedAuthorId = localStorage.getItem('noteAuthorId');
        return savedAuthorId ? parseInt(savedAuthorId, 10) : null;
    });
    const [selectedModel, setSelectedModel] = useState('');
    const [userAnnotation, setUserAnnotation] = useState('');
    const [noteText, setNoteText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sessionId, setSessionId] = useState(null);
    
    // Нові поля для метаданих
    const [category, setCategory] = useState('tip');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [priority, setPriority] = useState('medium');
    const [source, setSource] = useState('');
    const [relatedTo, setRelatedTo] = useState('');
    const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchAuthors();
            // Встановлюємо модель за замовчуванням
            if (availableModels.length > 0) {
                setSelectedModel(availableModels[0].id);
            }
        } else {
            // Скидаємо стан при закритті модального вікна
            setUserAnnotation('');
            setNoteText('');
            setCategory('tip');
            setTags([]);
            setTagInput('');
            setPriority('medium');
            setSource('');
            setRelatedTo('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const fetchAuthors = async () => {
        try {
            setLoading(true);
            const response = await AiAgentService.getAuthors();
            setAuthors(response || []);
            
            // Якщо є збережений автор, перевіряємо чи він існує в списку
            if (selectedAuthorId && response?.length > 0) {
                const authorExists = response.some(author => author.id === selectedAuthorId);
                if (!authorExists) {
                    // Якщо збереженого автора немає в списку, встановлюємо першого
                    setSelectedAuthorId(response[0].id);
                    localStorage.setItem('noteAuthorId', response[0].id.toString());
                }
            } else if (response && response.length > 0 && !selectedAuthorId) {
                // Якщо немає збереженого автора, встановлюємо першого
                setSelectedAuthorId(response[0].id);
            }
        } catch (error) {
            console.error('Error fetching authors:', error);
            setAuthors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateNote = async () => {
        setIsGenerating(true);
        setNoteText('');
        setSessionId(null);
        
        try {
            const response = await AiAgentService.generateNoteStream({
                user_question: userMessage,
                ai_response: aiMessage,
                corrections: userAnnotation
            });

            // Перевіряємо статус відповіді
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Помилка сервера: ${response.status} - ${errorText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let accumulatedText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            
                            // Обробка різних типів подій
                            if (data.event === 'on_start') {
                                console.log('Початок генерації:', data.data?.message);
                            } else if (data.event === 'on_token') {
                                // Додаємо токен до нотатки
                                const token = data.data?.token || '';
                                accumulatedText += token;
                                setNoteText(accumulatedText);
                            } else if (data.event === 'on_complete') {
                                // Генерація завершена
                                console.log('Генерацію завершено');
                                const fullTextData = JSON.parse(data.data?.full_text);
                                
                                if (fullTextData && typeof fullTextData === 'object') {
                                    // Парсимо об'єкт з відповіді
                                    const content = fullTextData.content || accumulatedText;
                                    const metadata = fullTextData.metadata || {};
                                    
                                    // Заповнюємо поле тексту нотатки
                                    setNoteText(content);
                                    
                                    // Заповнюємо метадані, якщо вони є
                                    if (metadata.category) {
                                        setCategory(metadata.category);
                                    }
                                    if (metadata.tags && Array.isArray(metadata.tags) && metadata.tags.length > 0) {
                                        setTags(metadata.tags);
                                    }
                                    if (metadata.priority) {
                                        setPriority(metadata.priority);
                                    }
                                    if (metadata.source) {
                                        setSource(metadata.source);
                                    }
                                    if (metadata.related_to) {
                                        setRelatedTo(metadata.related_to);
                                    }
                                } else {
                                    // Якщо full_text це просто рядок
                                    setNoteText(fullTextData || accumulatedText);
                                }
                                
                                setIsGenerating(false);
                            } else if (data.event === 'on_error') {
                                console.error('Stream error:', data.data?.error);
                                throw new Error(data.data?.error || 'Невідома помилка');
                            }
                        } catch (parseError) {
                            console.error('Error parsing SSE data:', parseError);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error generating note:', error);
            const errorMessage = error.message || 'Невідома помилка';
            setNoteText(`Помилка при генерації нотатки: ${errorMessage}\n\nСпробуйте ще раз.`);
        } finally {
            setIsGenerating(false);
            setSessionId(null);
        }
    };

    const handleStopGeneration = async () => {
        if (sessionId) {
            try {
                await AiAgentService.stopStream(sessionId);
                setIsGenerating(false);
                setSessionId(null);
            } catch (error) {
                console.error('Error stopping generation:', error);
            }
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !tags.includes(tagInput.trim())) {
            setTags([...tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleSaveNote = async () => {
        try {
            // Знаходимо ім'я автора за ID
            const selectedAuthor = authors.find(author => author.id === selectedAuthorId);
            const authorName = selectedAuthor?.name || 'admin';

            const noteData = {
                content: noteText, // Використовуємо згенерований текст
                metadata: {
                    author: authorName,
                    created_at: new Date().toISOString(),
                    category: category || undefined,
                    tags: tags.length > 0 ? tags : undefined,
                    priority: priority || undefined,
                    source: source.trim() || undefined,
                    related_to: relatedTo.trim() || undefined,
                    // Зберігаємо оригінальну інформацію з AI-відповіді, якщо вона є
                    user_annotation: userAnnotation.trim() || undefined,
                    original_user_question: userMessage || undefined,
                    original_ai_response: aiMessage || undefined
                }
            };

            await AiAgentService.createVectorNote(noteData);
            
            // Очищаємо форму та закриваємо модальне вікно
            setNoteText('');
            setUserAnnotation('');
            setSelectedModel(availableModels[0]?.id || '');
            setCategory('tip');
            setTags([]);
            setTagInput('');
            setPriority('medium');
            setSource('');
            setRelatedTo('');
            onClose();
        } catch (error) {
            console.error('Error saving note:', error);
            alert('Помилка при збереженні нотатки');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="note-modal-overlay" onClick={onClose}>
            <div className="note-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="note-modal-header">
                    <h2>Створення нотатки</h2>
                    <button className="close-button" onClick={onClose}>
                        <MdClose size={24} />
                    </button>
                </div>

                <div className="note-modal-body">
                    {loading ? (
                        <div className="loading-state">Завантаження...</div>
                    ) : (
                        <>
                            {/* Повідомлення користувача */}
                            <div className="note-section">
                                <label className="note-label">Повідомлення користувача:</label>
                                <div className="message-display user-message">
                                    {userMessage || 'Немає повідомлення'}
                                </div>
                            </div>

                            {/* Відповідь ШІ */}
                            <div className="note-section">
                                <label className="note-label">Відповідь ШІ:</label>
                                <div className="message-display ai-message-display">
                                    {aiMessage || 'Немає відповіді'}
                                </div>
                            </div>

                            {/* Анотація користувача */}
                            <div className="note-section">
                                <label className="note-label" htmlFor="user-annotation">
                                    Анотація (чому відповідь неправильна):
                                </label>
                                <textarea
                                    id="user-annotation"
                                    className="note-textarea"
                                    value={userAnnotation}
                                    onChange={(e) => setUserAnnotation(e.target.value)}
                                    placeholder="Опишіть, чому відповідь ШІ неправильна або де вона помилилась..."
                                    rows={4}
                                />
                            </div>

                            {/* Вибір автора */}
                            <div className="note-section">
                                <label className="note-label" htmlFor="author-select">
                                    Автор:
                                </label>
                                <select
                                    id="author-select"
                                    className="note-select"
                                    value={selectedAuthorId || ''}
                                    onChange={(e) => {
                                        const newAuthorId = parseInt(e.target.value, 10);
                                        setSelectedAuthorId(newAuthorId);
                                        localStorage.setItem('noteAuthorId', newAuthorId.toString());
                                    }}
                                >
                                    {authors.map((author) => (
                                        <option key={author.id} value={author.id}>
                                            {author.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Вибір моделі */}
                            {/* <div className="note-section">
                                <label className="note-label" htmlFor="model-select">
                                    Модель для генерації:
                                </label>
                                <select
                                    id="model-select"
                                    className="note-select"
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                >
                                    {availableModels.map((model) => (
                                        <option key={model.id} value={model.id}>
                                            {model.name}
                                        </option>
                                    ))}
                                </select>
                            </div> */}

                            {/* Кнопка генерації */}
                            <div className="note-section">
                                {!isGenerating ? (
                                    <button 
                                        className="generate-button"
                                        onClick={handleGenerateNote}
                                        disabled={!userAnnotation.trim()}
                                    >
                                        Згенерувати нотатку
                                    </button>
                                ) : (
                                    <button 
                                        className="stop-generation-button"
                                        onClick={handleStopGeneration}
                                    >
                                        Зупинити генерацію
                                    </button>
                                )}
                            </div>

                            {/* Поле редагування тексту - показуємо тільки якщо текст згенеровано або генерується */}
                            {(noteText || isGenerating) && (
                                <div className="note-section">
                                    <label className="note-label" htmlFor="note-text">
                                        Текст нотатки (згенерований ШІ):
                                        {isGenerating && <span className="generating-indicator"> Генерується...</span>}
                                    </label>
                                    <textarea
                                        id="note-text"
                                        className="note-textarea"
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        placeholder="Згенерований текст нотатки з'явиться тут або введіть вручну..."
                                        rows={6}
                                        disabled={isGenerating}
                                    />
                                </div>
                            )}

                            {/* Акордеон для метаданих */}
                            <div className="note-section">
                                <button 
                                    className="accordion-header"
                                    onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
                                    type="button"
                                >
                                    <span>Мета дані</span>
                                    <span className={`accordion-icon ${isMetadataExpanded ? 'expanded' : ''}`}>
                                        ▼
                                    </span>
                                </button>
                                
                                {isMetadataExpanded && (
                                    <div className="accordion-content">
                                        {/* Категорія */}
                                        <div className="note-section">
                                            <label className="note-label" htmlFor="category-select">
                                                Категорія:
                                            </label>
                                            <select
                                                id="category-select"
                                                className="note-select"
                                                value={category}
                                                onChange={(e) => setCategory(e.target.value)}
                                            >
                                                <option value="bug">Bug (Помилка)</option>
                                                <option value="feature">Feature (Функція)</option>
                                                <option value="tip">Tip (Порада)</option>
                                                <option value="warning">Warning (Попередження)</option>
                                            </select>
                                        </div>

                                        {/* Теги */}
                                        <div className="note-section">
                                            <label className="note-label" htmlFor="tag-input">
                                                Теги:
                                            </label>
                                            <div className="tag-input-container">
                                                <input
                                                    id="tag-input"
                                                    type="text"
                                                    className="note-input"
                                                    value={tagInput}
                                                    onChange={(e) => setTagInput(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAddTag();
                                                        }
                                                    }}
                                                    placeholder="Введіть тег і натисніть Enter або кнопку Додати"
                                                />
                                                <button 
                                                    type="button"
                                                    className="add-tag-button"
                                                    onClick={handleAddTag}
                                                >
                                                    Додати
                                                </button>
                                            </div>
                                            {tags.length > 0 && (
                                                <div className="tags-display">
                                                    {tags.map((tag, index) => (
                                                        <span key={index} className="tag-chip">
                                                            {tag}
                                                            <button
                                                                type="button"
                                                                className="remove-tag-button"
                                                                onClick={() => handleRemoveTag(tag)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Пріоритет */}
                                        <div className="note-section">
                                            <label className="note-label" htmlFor="priority-select">
                                                Пріоритет:
                                            </label>
                                            <select
                                                id="priority-select"
                                                className="note-select"
                                                value={priority}
                                                onChange={(e) => setPriority(e.target.value)}
                                            >
                                                <option value="low">Low (Низький)</option>
                                                <option value="medium">Medium (Середній)</option>
                                                <option value="high">High (Високий)</option>
                                            </select>
                                        </div>

                                        {/* Джерело */}
                                        <div className="note-section">
                                            <label className="note-label" htmlFor="source-input">
                                                Джерело (опціонально):
                                            </label>
                                            <input
                                                id="source-input"
                                                type="text"
                                                className="note-input"
                                                value={source}
                                                onChange={(e) => setSource(e.target.value)}
                                                placeholder="Наприклад: документація, код, обговорення"
                                            />
                                        </div>

                                        {/* Пов'язано з */}
                                        <div className="note-section">
                                            <label className="note-label" htmlFor="related-to-input">
                                                Пов'язано з (опціонально):
                                            </label>
                                            <input
                                                id="related-to-input"
                                                type="text"
                                                className="note-input"
                                                value={relatedTo}
                                                onChange={(e) => setRelatedTo(e.target.value)}
                                                placeholder="Наприклад: файли або модулі"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="note-modal-footer">
                    <button className="cancel-button" onClick={onClose}>
                        Скасувати
                    </button>
                    <button 
                        className="save-button" 
                        onClick={handleSaveNote}
                        disabled={!noteText.trim() || !selectedAuthorId || loading || isGenerating}
                    >
                        Зберегти
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoteCreationModal;
